import { useState } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, ArrowUpRight, Check, AlertTriangle, Loader2 } from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import { Reveal } from '../components/Motion';
import { COMPANY } from '../data/site';
import { CONTACT as CT } from '../constants/testIds';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_OPTS = ['Serrurerie', 'Métallerie', 'Vitrerie', 'Dépannage', 'Autre'];
const URGENCY_OPTS = [
  { v: 'non_urgent', l: 'Projet non urgent' },
  { v: 'to_plan', l: 'Intervention à planifier' },
  { v: 'urgent', l: 'Demande urgente' },
];

const initial = {
  first_name: '', last_name: '', phone: '', email: '',
  service: 'Serrurerie', urgency: 'to_plan',
  address: '', city: '', postal_code: '',
  description: '', budget: '', desired_date: '',
  consent: false, website: '',
};

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const update = (k) => (e) => {
    const val = e && e.target && e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    if (!form.consent) { setErrorMsg('Vous devez accepter la politique de confidentialité.'); return; }
    setStatus('loading');
    try {
      const { data } = await axios.post(`${API}/contact`, form);
      if (data && data.success) {
        setStatus('success');
        setForm(initial);
      } else {
        setStatus('error');
        setErrorMsg("Une erreur inattendue est survenue.");
      }
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || "L’envoi a échoué. Merci de réessayer ou de nous appeler.");
    }
  };

  const input = 'w-full bg-transparent border-b border-white/20 focus:border-white/70 outline-none py-3 px-0 text-white placeholder:text-white/30 transition-colors';
  const label = 'font-mono text-[10px] tracking-[0.22em] uppercase text-white/60';

  return (
    <>
      <section className="relative pt-24 md:pt-32 pb-14 overflow-hidden">
        <AnimatedBackground />
        <div className="relative z-10 max-w-[1440px] mx-auto px-5 md:px-10">
          <Reveal>
            <div className="overline mb-4">◍ Contact</div>
            <h1 className="font-display italic text-[13vw] sm:text-[10vw] lg:text-[8vw] leading-[0.9]">
              Parlons de votre <em className="not-italic underline decoration-white/40 underline-offset-8">projet</em>.
            </h1>
            <p className="mt-6 text-white/60 text-lg max-w-2xl">
              Décrivez-nous votre besoin, nous vous répondons rapidement avec une proposition adaptée.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="relative py-16 md:py-24">
        <div className="max-w-[1440px] mx-auto px-5 md:px-10 grid grid-cols-12 gap-10">
          {/* Coordinates */}
          <aside className="col-span-12 lg:col-span-4 space-y-8">
            <div className="glass border border-white/10 p-8">
              <div className="overline mb-4">◍ Coordonnées</div>
              <ul className="space-y-4 text-white/80">
                <li className="flex items-start gap-3"><MapPin size={16} strokeWidth={1.5} className="mt-1" /><span>{COMPANY.address}<br />{COMPANY.city}</span></li>
                <li><a href={`tel:${COMPANY.phoneTel}`} className="flex items-center gap-3 link-underline"><Phone size={16} strokeWidth={1.5} /> {COMPANY.phone}</a></li>
                <li><a href={`mailto:${COMPANY.email}`} className="flex items-center gap-3 link-underline break-all"><Mail size={16} strokeWidth={1.5} /> {COMPANY.email}</a></li>
              </ul>
              <div className="mt-8 pt-6 border-t border-white/10">
                <div className={label + ' mb-3'}>Horaires</div>
                <div className="text-white/80 text-sm leading-relaxed">
                  Du lundi au vendredi<br />
                  De 8h00 à 18h00
                </div>
              </div>
            </div>
            <div className="relative aspect-[4/3] overflow-hidden border border-white/10">
              <iframe
                title="Carte — 49 rue de Ponthieu, 75008 Paris"
                src={COMPANY.mapEmbed}
                width="100%" height="100%" loading="lazy" style={{ border: 0, filter: 'grayscale(100%) contrast(1.1) invert(0.92)' }}
                referrerPolicy="no-referrer-when-downgrade"
              />
              <div className="absolute inset-4 border border-white/15 pointer-events-none" />
            </div>
          </aside>

          {/* Form */}
          <form
            data-testid={CT.form}
            onSubmit={submit}
            className="col-span-12 lg:col-span-8 glass border border-white/10 p-6 md:p-10 space-y-8"
          >
            {/* Honeypot */}
            <input type="text" name="website" autoComplete="off" tabIndex={-1} value={form.website} onChange={update('website')} className="hidden" aria-hidden />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className={label}>Prénom *</div>
                <input required data-testid={CT.firstName} value={form.first_name} onChange={update('first_name')} className={input} placeholder="Jean" />
              </div>
              <div>
                <div className={label}>Nom *</div>
                <input required data-testid={CT.lastName} value={form.last_name} onChange={update('last_name')} className={input} placeholder="Dupont" />
              </div>
              <div>
                <div className={label}>Téléphone *</div>
                <input required data-testid={CT.phone} value={form.phone} onChange={update('phone')} className={input} placeholder="06 12 34 56 78" />
              </div>
              <div>
                <div className={label}>E-mail *</div>
                <input required type="email" data-testid={CT.email} value={form.email} onChange={update('email')} className={input} placeholder="jean@exemple.fr" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className={label}>Service concerné *</div>
                <select required data-testid={CT.service} value={form.service} onChange={update('service')} className={input + ' cursor-pointer appearance-none pr-6'} style={{ backgroundImage: 'none' }}>
                  {SERVICE_OPTS.map((s) => <option key={s} value={s} className="bg-black">{s}</option>)}
                </select>
              </div>
              <div>
                <div className={label}>Niveau d’urgence *</div>
                <select required data-testid={CT.urgency} value={form.urgency} onChange={update('urgency')} className={input + ' cursor-pointer appearance-none pr-6'}>
                  {URGENCY_OPTS.map((u) => <option key={u.v} value={u.v} className="bg-black">{u.l}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <div className={label}>Adresse de l’intervention</div>
                <input data-testid={CT.address} value={form.address} onChange={update('address')} className={input} placeholder="49 rue de Ponthieu" />
              </div>
              <div>
                <div className={label}>Ville</div>
                <input data-testid={CT.city} value={form.city} onChange={update('city')} className={input} placeholder="Paris" />
              </div>
              <div>
                <div className={label}>Code postal</div>
                <input data-testid={CT.postal} value={form.postal_code} onChange={update('postal_code')} className={input} placeholder="75008" />
              </div>
            </div>

            <div>
              <div className={label}>Description du projet *</div>
              <textarea required data-testid={CT.description} value={form.description} onChange={update('description')} rows={5} className={input + ' resize-none'} placeholder="Décrivez votre besoin, la nature de l’intervention, les contraintes du lieu…" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className={label}>Budget indicatif</div>
                <input data-testid={CT.budget} value={form.budget} onChange={update('budget')} className={input} placeholder="—" />
              </div>
              <div>
                <div className={label}>Date souhaitée</div>
                <input data-testid={CT.date} type="date" value={form.desired_date} onChange={update('desired_date')} className={input} />
              </div>
            </div>

            <label className="flex items-start gap-3 cursor-pointer">
              <input required type="checkbox" data-testid={CT.consent} checked={form.consent} onChange={update('consent')} className="mt-1 h-4 w-4 accent-white" />
              <span className="text-sm text-white/70 leading-relaxed">
                J’accepte que mes données soient utilisées pour traiter ma demande, conformément à la
                <a href="/confidentialite" className="underline decoration-white/40 underline-offset-2 ml-1">politique de confidentialité</a>.
              </span>
            </label>

            {status === 'error' && (
              <div data-testid={CT.error} className="flex items-start gap-3 border border-red-400/40 bg-red-400/5 p-4 text-sm text-red-100/90">
                <AlertTriangle size={16} className="mt-0.5" strokeWidth={1.5} />
                <span>{errorMsg}</span>
              </div>
            )}
            {status === 'success' && (
              <motion.div
                initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                data-testid={CT.success}
                className="flex items-start gap-3 border border-white/25 bg-white/5 p-4 text-sm text-white/90"
              >
                <Check size={16} className="mt-0.5" strokeWidth={1.5} />
                <span>Votre demande a bien été transmise. Ponthieu Pose et Rénovation vous recontactera dans les meilleurs délais.</span>
              </motion.div>
            )}

            <button
              type="submit"
              disabled={status === 'loading'}
              data-testid={CT.submit}
              className="shine-wrap w-full md:w-auto inline-flex items-center justify-center gap-3 bg-white text-black px-6 h-14 font-mono text-[11px] tracking-[0.22em] uppercase disabled:opacity-60"
            >
              {status === 'loading' ? (<><Loader2 size={16} strokeWidth={1.5} className="animate-spin" /> Envoi…</>) : (<>Envoyer la demande <ArrowUpRight size={14} strokeWidth={1.5} /></>)}
            </button>
          </form>
        </div>
      </section>
    </>
  );
}
